import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import type { PaletteColor, ThemeOptions } from '@mui/material';
import { createTheme, darken, lighten, ThemeProvider } from '@mui/material';
import { orange } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { FC } from 'react';
import { useMemo } from 'react';

import type { PanelInfo } from './declarations/ah-api';
import type { DeepReadonly } from './declarations/deep-readonly';
import { useSurveyContextSelector } from './survey-context';

declare module '@mui/material' {
  interface Palette {
    wbBackgroundColor: Palette['primary'];
    wbHeaderColor: Palette['primary'];
    wbBtnColor: Palette['primary'];
    cbBackgroundColor: Palette['primary'];
    cbHeaderColor: Palette['primary'];
    cbPrimary: Palette['primary']; // cbBtnColor
    cbSecondary: Palette['primary'];
  }
  interface PaletteOptions {
    wbBackgroundColor: PaletteOptions['primary'];
    wbHeaderColor: PaletteOptions['primary'];
    wbBtnColor: PaletteOptions['primary'];
    cbBackgroundColor: PaletteOptions['primary'];
    cbHeaderColor: PaletteOptions['primary'];
    cbPrimary: PaletteOptions['primary']; // cbBtnColor
    cbSecondary: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    cbPrimary: true;
    cbSecondary: true;
  }
  interface ButtonClasses {
    containedCbPrimary: ButtonClasses['containedPrimary'];
    containedCbSecondary: ButtonClasses['containedSecondary'];
  }
}

export const SurveyThemeProvider: FC = ({ children }) => {
  const panelInfo = useSurveyContextSelector((sc) => sc.args.panelInfo);
  const theme = useMemo(() => createTheme(getThemeOptionsFromPanelInfo(panelInfo)), [panelInfo]);
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>{children}</LocalizationProvider>
    </ThemeProvider>
  );
};

const ppDefaults = {
  cb_background_color: '#475370', // secondary color
  cb_button_color: '#1DC9BC', // color of a button used on colored background
  cb_button_text_color: '#FFFFFF', // needs to contrast with cb_button_color
  cb_header_text_color: '#1DC9BC', // color of header texts on colored background
  cb_regular_text_color: '#FFFFFF', // color of regular texts on colored background
  wb_background_color: '#FFFFFF', // white by default
  wb_button_color: '#1DC9BC', // usually same as primary color
  wb_button_text_color: '#FFFFFF', // white by default, needs to contrast with wb_button_color
  wb_header_text_color: '#1DC9BC', // primary color
  wb_regular_text_color: '#000000', // black by default
};

export const getThemeOptionsFromPanelInfo = (panelInfo: DeepReadonly<PanelInfo> | null | undefined): ThemeOptions => {
  const wbBackgroundColor = panelInfo?.app_theme?.wb_background_color || ppDefaults.wb_background_color;
  const wbHeaderColor = panelInfo?.app_theme?.wb_header_text_color || ppDefaults.wb_header_text_color;
  const wbTextColor = panelInfo?.app_theme?.wb_regular_text_color || ppDefaults.wb_regular_text_color;

  const wbBtnColor = panelInfo?.app_theme?.wb_button_color || ppDefaults.wb_button_color;
  const wbBtnTextColor = panelInfo?.app_theme?.wb_button_text_color || ppDefaults.wb_button_text_color;

  const cbBackgroundColor = panelInfo?.app_theme?.cb_background_color || ppDefaults.cb_background_color;
  const cbHeaderColor = panelInfo?.app_theme?.cb_header_text_color || ppDefaults.cb_header_text_color;
  const cbTextColor = panelInfo?.app_theme?.cb_regular_text_color || ppDefaults.cb_regular_text_color;

  const cbBtnColor = panelInfo?.app_theme?.cb_button_color || ppDefaults.cb_button_color;
  const cbBtnTextColor = panelInfo?.app_theme?.cb_button_text_color || ppDefaults.cb_button_text_color;
  const secondaryColor = panelInfo?.app_theme.color_secondary || '#4169E1';

  const tonalOffset = 0.15;
  const fontSize = 14;
  const themeOptions: ThemeOptions = {
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            '&.MuiOutlinedInput-root': {
              padding: 0,
            },
            marginTop: '8px',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
          variant: 'contained',
        },
        styleOverrides: {
          containedCbPrimary: ({ theme }) => containedBtn(theme.palette.cbPrimary),
          containedCbSecondary: ({ theme }) => containedBtn(reverseColor(theme.palette.cbPrimary, tonalOffset)),
          containedPrimary: ({ theme }) => containedBtn(theme.palette.wbBtnColor),
          containedSecondary: ({ theme }) => containedBtn(reverseColor(theme.palette.wbBtnColor, tonalOffset)),
          root: {
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              opacity: 0.5,
              pointerEvents: 'auto',
            },
            '&:hover': {
              boxShadow: 'none',
            },
            boxShadow: 'none',
            fontSize,
            textTransform: 'none',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          colorPrimary: ({ theme }) => ({
            color: theme.palette.wbBtnColor.main, // unchecked color
            '&.Mui-checked': {
              color: theme.palette.info.main, // checked color
            },
          }),
          colorSecondary: ({ theme }) => ({
            color: theme.palette.secondary.main, // unchecked color
            '&.Mui-checked': {
              color: theme.palette.secondary.main, // checked color
            },
          }),
          root: {
            // unset shadow on hover
            '&:hover': {
              backgroundColor: 'unset',
            },
            '&.Mui-checked:hover': {
              backgroundColor: 'unset',
            },
            border: '0px',
            padding: '9px',
            '&:focus': {
              border: '1px solid',
              padding: '8px',
            },
          },
        },
      },
      MuiInput: {
        defaultProps: {
          autoComplete: 'off',
          disableUnderline: true,
          spellCheck: false,
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            position: 'initial',
            transform: 'initial',
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          IconComponent: ExpandMoreTwoToneIcon,
          MenuProps: {
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
            style: {
              marginTop: '1px',
              maxHeight: '400px',
            },
            transformOrigin: {
              horizontal: 'left',
              vertical: 'top',
            },
          },
          variant: 'outlined',
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            display: 'flex',
            height: '20px',
            padding: 0,
            width: '36px',
          },
          switchBase: ({ theme }) => ({
            padding: 2,
            '&.Mui-checked': {
              color: theme.palette.wbBtnColor.contrastText,
              transform: 'translateX(16px)',
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.wbBtnColor.main,
                borderColor: theme.palette.wbBtnColor.main,
                opacity: 1,
              },
            },
          }),
          thumb: {
            // this is the moving circle
            width: '16px',
            height: '16px',
            boxShadow: 'none',
          },
          track: {
            // this is style when unchecked
            borderColor: '#CBCBD1',
            backgroundColor: '#CBCBD1',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: 20 / 2,
            opacity: 1,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          autoComplete: 'off',
          margin: 'none',
          variant: 'outlined',
        },
      },
    },
    palette: {
      cbBackgroundColor: {
        contrastText: cbTextColor,
        dark: darken(cbBackgroundColor, tonalOffset),
        light: lighten(cbBackgroundColor, tonalOffset),
        main: cbBackgroundColor,
      },
      cbHeaderColor: {
        contrastText: cbBackgroundColor, // since text will be used on this background, it should contrast with it
        dark: darken(cbHeaderColor, tonalOffset),
        light: lighten(cbHeaderColor, tonalOffset),
        main: cbHeaderColor,
      },
      cbPrimary: {
        contrastText: cbBtnTextColor,
        dark: darken(cbBtnColor, tonalOffset),
        light: lighten(cbBtnColor, tonalOffset),
        main: cbBtnColor,
      },
      cbSecondary: {
        contrastText: cbBtnTextColor,
        dark: darken(cbBtnColor, tonalOffset),
        light: lighten(cbBtnColor, tonalOffset),
        main: cbBtnColor,
      },
      error: {
        contrastText: '#FFFFFF',
        main: '#F22C44',
      },
      info: {
        contrastText: '#FFFFFF',
        main: secondaryColor,
      },
      primary: {
        contrastText: wbBackgroundColor,
        main: wbHeaderColor,
      },
      secondary: {
        contrastText: cbTextColor,
        main: cbBackgroundColor,
      },
      success: {
        contrastText: '#FFFFFF',
        main: '#2DCC70',
      },
      tonalOffset,
      warning: {
        main: orange.A400,
      },
      wbBackgroundColor: {
        contrastText: wbTextColor,
        dark: darken(wbBackgroundColor, tonalOffset),
        light: lighten(wbBackgroundColor, tonalOffset),
        main: wbBackgroundColor,
      },
      wbBtnColor: {
        contrastText: wbBtnTextColor,
        dark: darken(wbBtnColor, tonalOffset),
        light: lighten(wbBtnColor, tonalOffset),
        main: wbBtnColor,
      },
      wbHeaderColor: {
        contrastText: wbBackgroundColor, // since text will be used on this background, it should contrast with it
        dark: darken(wbHeaderColor, tonalOffset),
        light: lighten(wbHeaderColor, tonalOffset),
        main: wbHeaderColor,
      },
    },
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize,
      fontWeightMedium: 600,
    },
  };
  return themeOptions;
};

const reverseColor = (clr: PaletteColor, tonalOffset: number): PaletteColor => ({
  contrastText: clr.main,
  dark: darken(clr.contrastText, tonalOffset / 2),
  light: lighten(clr.contrastText, tonalOffset),
  main: clr.contrastText,
});

const containedBtn = (clr: PaletteColor) => ({
  '&.Mui-disabled': {
    '&:hover': {
      backgroundColor: clr.main,
      color: clr.contrastText,
    },
    backgroundColor: clr.main,
    color: clr.contrastText,
  },
  '&.PP-loading': {
    '&:hover': {
      color: clr.main,
    },
    color: clr.main,
  },
  '&.PP-loading .MuiCircularProgress-root': {
    color: clr.contrastText,
    position: 'absolute',
  },
  '&:hover': {
    backgroundColor: clr.light,
    color: clr.contrastText,
  },
  backgroundColor: clr.main,
  color: clr.contrastText,
});
